import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { FloppyDisk, QrCode } from "@phosphor-icons/react";

const EMPTY = {
  name: "", phone: "", account_holder: "", bank_name: "",
  account_number: "", ifsc: "", upi_id: "",
};

function UpiQrCard({ upi, payeeName }) {
  const upiString = upi
    ? `upi://pay?pa=${encodeURIComponent(upi)}&pn=${encodeURIComponent(payeeName)}&cu=INR`
    : "";
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-6 h-fit">
      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-3 flex items-center gap-1.5">
        <QrCode size={14} weight="duotone" /> UPI payment QR
      </div>
      {upiString ? (
        <div className="text-center" data-testid="upi-qr-card">
          <div className="inline-block p-3 bg-white border border-zinc-200 rounded-md">
            <QRCodeSVG value={upiString} size={200} data-testid="account-upi-qr" />
          </div>
          <div className="mt-3 font-mono text-sm text-zinc-800">{upi}</div>
          <p className="text-xs text-zinc-500 mt-1">
            This QR is auto-generated from your UPI ID. Finance scans it to pay you.
          </p>
        </div>
      ) : (
        <p className="text-sm text-zinc-500" data-testid="upi-qr-empty">
          Enter and save your UPI ID — a payment QR code will appear here automatically.
        </p>
      )}
    </div>
  );
}

export default function Account() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "", phone: user.phone || "",
        account_holder: user.account_holder || "", bank_name: user.bank_name || "",
        account_number: user.account_number || "", ifsc: user.ifsc || "",
        upi_id: user.upi_id || "",
      });
    }
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/me/account", form);
      updateUser(data);
      toast.success("Account details saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const upi = (form.upi_id || "").trim();
  const payeeName = form.account_holder || form.name || "";

  return (
    <DashboardLayout>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Profile</div>
        <h1 className="font-heading text-2xl font-semibold text-zinc-900">My account</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Update your bank details & UPI ID. Finance uses these to pay your bills.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 max-w-4xl">
        <form onSubmit={save} className="bg-white border border-zinc-200 rounded-lg p-6 space-y-4" data-testid="account-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="acc-name">Full name</Label>
              <Input id="acc-name" data-testid="acc-name" value={form.name} onChange={set("name")} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="acc-phone">Phone number</Label>
              <Input id="acc-phone" data-testid="acc-phone" value={form.phone} onChange={set("phone")} className="mt-1 font-mono" />
            </div>
            <div>
              <Label htmlFor="acc-holder">Account holder name</Label>
              <Input id="acc-holder" data-testid="acc-holder" value={form.account_holder} onChange={set("account_holder")} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="acc-bank">Bank name</Label>
              <Input id="acc-bank" data-testid="acc-bank" value={form.bank_name} onChange={set("bank_name")} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="acc-number">Bank account number</Label>
              <Input id="acc-number" data-testid="acc-number" value={form.account_number} onChange={set("account_number")} className="mt-1 font-mono" />
            </div>
            <div>
              <Label htmlFor="acc-ifsc">IFSC code</Label>
              <Input id="acc-ifsc" data-testid="acc-ifsc" value={form.ifsc} onChange={set("ifsc")} className="mt-1 font-mono uppercase" />
            </div>
            <div className="col-span-2">
              <Label htmlFor="acc-upi">UPI ID</Label>
              <Input id="acc-upi" data-testid="acc-upi" value={form.upi_id} onChange={set("upi_id")}
                placeholder="e.g. yourname@okhdfcbank" className="mt-1 font-mono" />
            </div>
          </div>
          <Button type="submit" disabled={saving} data-testid="save-account-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white">
            <FloppyDisk size={16} className="mr-1.5" /> {saving ? "Saving…" : "Save details"}
          </Button>
        </form>

        <UpiQrCard upi={upi} payeeName={payeeName} />
      </div>
    </DashboardLayout>
  );
}
